import { AnalyticsBrowser } from '@segment/analytics-next';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import type { RootState } from '../store';
import getConfig from '../selectors/getConfig';
import { _getProfile } from '../selectors/getProfile';
import { CustomContextMapper } from './CustomContextMapper';
import { defaultStore, RedcatAnalyticsContext } from './RedcatAnalyticsContext';
import { createContextEnrichmentPlugin } from './plugins/ContextEnrichmentPlugin';
import type { CustomContext } from './types/context/customContext';
import type { RedcatAnalyticsStore } from './types/store';

const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

const noop = () => undefined;

// Segment posts events to `https://${apiHost}/...`; strip the scheme from the proxy base and
// append `/v1` so traffic is routed first-party through the proxy instead of api.segment.io.
function deriveApiHost(baseUrl: string): string {
  const host = baseUrl.replace(/^https?:\/\//, '').replace(/\/+$/, '');
  return `${host}/v1`;
}

interface Engine {
  analytics: AnalyticsBrowser;
  enrichContext: (partial: Partial<CustomContext>) => void;
  plugin: ReturnType<typeof createContextEnrichmentPlugin>['plugin'];
}

function createEngine(): Engine {
  const analytics = new AnalyticsBrowser();
  const initialContext: CustomContext = { surface: 'web', sessionId: uuidv4() };
  const { plugin, enrichContext } = createContextEnrichmentPlugin(initialContext);
  return { analytics, enrichContext, plugin };
}

export function RedcatAnalyticsProvider({ children }: { children: React.ReactNode }) {
  // Sourced solely from the backend app config (cdp_cfg) — fetched async via the `sync` thunk, so
  // both start out null until that resolves. No fallback: while unset, analytics stays a no-op.
  const cdpWriteKey = useSelector((state: RootState) => getConfig(state).cdpWriteKey);
  const cdpBaseUrl = useSelector((state: RootState) => getConfig(state).cdpBaseUrl);
  const isConfigured = Boolean(cdpWriteKey && cdpBaseUrl);

  // Engine is created lazily once config resolves. Using state (not a ref) so the component
  // re-renders and the memoized store below swaps from the no-op defaultStore to the real one.
  const [engine, setEngine] = useState<Engine | null>(null);
  useEffect(() => {
    if (!isConfigured || engine) {
      return;
    }
    setEngine(createEngine());
  }, [isConfigured, engine]);

  useEffect(() => {
    if (!engine || !cdpWriteKey || !cdpBaseUrl) {
      return undefined;
    }

    engine.analytics.register(engine.plugin).catch(noop);
    engine.analytics
      .load(
        { writeKey: cdpWriteKey, cdnURL: cdpBaseUrl },
        {
          integrations: {
            'Segment.io': {
              apiHost: deriveApiHost(cdpBaseUrl),
              // Our self-hosted Jitsu ingest only implements the batch endpoint (/v1/b);
              // the SDK's default "standard" strategy posts per-event to /v1/t, /v1/i, etc.,
              // which 401 with no CORS headers on this deployment.
              deliveryStrategy: { strategy: 'batching' },
            },
          },
          // analytics-next's internal telemetry beacon posts to `${apiHost}/m`, which our
          // self-hosted Jitsu ingest doesn't implement (Segment-only endpoint) - it 404s
          // without CORS headers and surfaces as a misleading CORS error. Disable it.
          updateCDNSettings: settings => ({
            ...settings,
            metrics: { ...settings.metrics, sampleRate: 0 },
          }),
        },
      )
      .catch(noop);

    // Web equivalent of the mobile AppState listener. analytics-next flushes queued events on
    // unload itself (keepalive), so we only track backgrounded time and rotate the session when
    // the user returns after 30+ minutes away.
    let backgroundedAt: number | null = null;
    const onVisibilityChange = () => {
      if (document.hidden) {
        backgroundedAt = Date.now();
        return;
      }
      if (backgroundedAt && Date.now() - backgroundedAt >= SESSION_TIMEOUT_MS) {
        engine.enrichContext({ sessionId: uuidv4() });
      }
      backgroundedAt = null;
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [engine, cdpWriteKey, cdpBaseUrl]);

  // Every logout path (sign-out button, SIGN_OUT auth hook, bad-auth-token clearCookies) ends in
  // clearProfile, so reset on the signed-in -> signed-out transition rather than at each call site.
  // Guests never have a profile, so this never rotates a guest's anonymousId.
  const signedIn = useSelector((state: RootState) => Boolean(_getProfile(state)));
  const wasSignedIn = useRef(signedIn);
  useEffect(() => {
    if (wasSignedIn.current && !signedIn) {
      engine?.analytics.reset();
    }
    wasSignedIn.current = signedIn;
  }, [signedIn, engine]);

  const store = useMemo<RedcatAnalyticsStore>(() => {
    if (!engine) {
      return defaultStore;
    }

    const { analytics, enrichContext } = engine;

    return {
      isConfigured: true,
      track: (event, contextOverride) => {
        analytics
          .track(event.name, event.properties ?? {}, CustomContextMapper.toOptions(contextOverride))
          .catch(noop);
      },
      page: (event, contextOverride) => {
        analytics
          .page(
            event.category ?? null,
            event.name,
            event.properties ?? {},
            CustomContextMapper.toOptions(contextOverride),
          )
          .catch(noop);
      },
      identify: (event, contextOverride) => {
        analytics
          .identify(
            event.userId,
            event.traits ?? {},
            CustomContextMapper.toOptions(contextOverride),
          )
          .catch(noop);
      },
      alias: (newUserId, contextOverride) => {
        // Pass previousId explicitly. Left undefined, the Segment.io plugin fills it in at send
        // time as `user.id() ?? anonymousId` — by which point the identify() that follows alias()
        // has already set user.id(), so previous_id came out equal to the new userId.
        const options = CustomContextMapper.toOptions(contextOverride);
        if (analytics.instance) {
          const previousId = analytics.instance.user().anonymousId() ?? undefined;
          analytics.alias(newUserId, previousId, options).catch(noop);
          return;
        }
        // Not loaded yet: user() is buffered until load, then resolves with the persisted user.
        analytics
          .user()
          .then(user => analytics.alias(newUserId, user.anonymousId() ?? undefined, options))
          .catch(noop);
      },
      group: (event, contextOverride) => {
        analytics
          .group(event.groupId, event.traits ?? {}, CustomContextMapper.toOptions(contextOverride))
          .catch(noop);
      },
      updateContext: context => enrichContext(context),
      reset: () => analytics.reset(),
    };
  }, [engine]);

  return (
    <RedcatAnalyticsContext.Provider value={store}>{children}</RedcatAnalyticsContext.Provider>
  );
}
